import { html, SP } from '../../html.js';
import type { VNode } from '../../html.js';
import { CopyButton } from '../../ui/copy.js';
import type {
  OpenChallengeRecord,
  OwnershipRecord,
  ProofChallenge,
  ProofVerification,
} from '../api.js';

/**
 * Proving you own your own domain — ONE component, two surfaces.
 *
 * It used to be one surface. The whole `/api/ownership/*` API was mounted and
 * working, and the only screen that called it was the publish gate on the public
 * console's result page — a screen you can only reach by running a fresh crawl.
 * So a customer whose claim had expired had to re-crawl their own origin to
 * renew it, or type `curl`, and the site screen in the dashboard printed "no
 * claim is on record" for every site on every host, including the verified ones.
 *
 * This file is the fix, and it lives here rather than beside the gate so the two
 * callers cannot drift again: the gate hands it the state it already polls, the
 * site screen hands it the claim the tenant payload now carries, and neither one
 * words the instructions itself.
 *
 * TWO CLASSES ON EVERY ELEMENT, which is the one thing about this file that
 * looks like a mistake and is not. The two surfaces have two stylesheets and no
 * shared vocabulary: the public console is committed to its own dark ledger
 * palette in `console.css`, the dashboard is painted from `packages/design-tokens`
 * through `dashboard.css`. So each element carries the console's existing hook —
 * `disclaimer`, `secret__hint`, `ledger2__row` — and this component's own
 * `proof__*` hook beside it. The dashboard paints the `proof__*` half from the
 * token layer. Neither sheet has to learn the other's names, and neither surface
 * renders unstyled.
 *
 * The three declarations `console.css` has no name for — the gap between the
 * paragraphs, the single column the attempt block sits in, and the monospaced
 * token — travel as inline styles under `surface: 'console'`, which is where
 * `gate.ts` carried them before this component existed. They are NOT applied on
 * the dashboard: `10px` and `12px` are off the token scale, and `dashboard.css`
 * owns those three decisions in `--s-*` and `--t-code` instead.
 *
 * NOTHING HERE PUBLISHES. Verifying changes what MAY be published; a person
 * still presses publish. That is the API's own sentence and it is printed as it
 * was sent.
 */

/**
 * The state of a claim as a person would say it, not as a column stores it.
 *
 * `state` alone is never the answer: a claim can read `verified` and be expired,
 * or be about an origin the tenant no longer serves. `current` is the field that
 * agrees with what `/publish` will actually do, so it decides first and the
 * stored word only explains the ways it can be false.
 */
export type ClaimState =
  | 'none'
  | 'pending'
  | 'verified'
  | 'expired'
  | 'moved'
  | 'revoked'
  | 'failed';

export function claimStateOf(
  claim: OwnershipRecord | null,
  open: OpenChallengeRecord | null,
): ClaimState {
  if (!claim) return open ? 'pending' : 'none';
  if (claim.state === 'revoked') return 'revoked';
  if (claim.current) return 'verified';
  if (claim.originDrift) return 'moved';
  if (claim.daysUntilExpiry != null && claim.daysUntilExpiry <= 0) return 'expired';
  return 'failed';
}

/** One sentence per state, in the second person, saying what is true right now. */
function summarise(
  state: ClaimState,
  domain: string,
  claim: OwnershipRecord | null,
  open: OpenChallengeRecord | null,
): string {
  switch (state) {
    case 'verified': {
      const days = claim?.daysUntilExpiry;
      const when =
        typeof days === 'number'
          ? `, good for another ${days} ${days === 1 ? 'day' : 'days'}`
          : '';
      return `Control of ${domain} is verified by ${claim?.method ?? 'a proof'}${when}. This site may be published under your own account, with no disclaimer.`;
    }
    case 'pending':
      return `A challenge is open for ${open?.domain || domain}${
        open && open.attempts > 0
          ? ` (${open.attempts} ${open.attempts === 1 ? 'attempt' : 'attempts'} so far)`
          : ''
      }. Publish one of the three proofs below and press Check now.`;
    case 'expired':
      return `The claim on ${domain} has lapsed. The proof you published is probably still in place — Check now re-reads it and, if it is there, the claim is current again.`;
    case 'moved':
      return `The claim on ${domain} no longer covers what this site serves: ${claim?.originDrift}. A claim covers an origin, so this one has to be made again from a new challenge.`;
    case 'revoked':
      return `The claim on ${domain} was revoked by the operator${
        claim?.revokedReason ? ` — ${claim.revokedReason}` : ''
      }. Verifying again from a new challenge is the way back.`;
    case 'failed':
      return `The last check of ${domain} did not find the proof${
        claim?.lastCheckDetail ? `: ${claim.lastCheckDetail}` : ''
      }. The claim stands until it lapses; a successful check restores it.`;
    default:
      return `No claim is on record for ${domain}. A claim is what lets this site be published under your own account without the third-party disclaimer, and what lets a form on it be relayed. Any one of three proofs is enough.`;
  }
}

/**
 * The word on the state chip. Short enough to read without stopping.
 *
 * `.chip` is the one class both stylesheets happen to define, so the badge is
 * the single element here that needs no second hook. Only `dashboard.css` reads
 * the `data-tone`; on the public console the chip is uniformly dim and the
 * sentence directly under it carries the difference, which it does on both.
 */
function badge(state: ClaimState): string {
  switch (state) {
    case 'verified':
      return 'Verified';
    case 'pending':
      return 'Awaiting a proof';
    case 'expired':
      return 'Expired';
    case 'moved':
      return 'Origin moved';
    case 'revoked':
      return 'Revoked';
    case 'failed':
      return 'Last check failed';
    default:
      return 'Not proven';
  }
}

/**
 * What a refusal MEANS, beside the verifier's own words for what it saw.
 *
 * The detail is precise and answers "what happened"; these answer "so what do I
 * do", which is the half a person has to guess at otherwise. Codes this build
 * has never seen get nothing rather than a generic sentence — the detail is
 * always more useful than an invented gloss.
 */
function meaning(code: string): string | null {
  switch (code) {
    case 'no_record':
      return 'The record is not visible to a resolver yet. DNS changes are cached; a first miss minutes after publishing one is normal, an hour later is not.';
    case 'token_mismatch':
      return 'Something is at that name, but not this token. A stale record from an earlier challenge is the usual cause — the newest token is the only one that counts.';
    case 'no_tag':
      return 'The page loaded and the meta tag was not in its <head>. A tag inserted by JavaScript will not be seen: the verifier reads the HTML the server sent.';
    case 'not_published':
      return 'The URL answers 404. For the file proof the path has to be served exactly as given, with no redirect to a pretty error page.';
    case 'bad_status':
      return 'The origin answered, but not with the document. A login wall, a country redirect or a WAF in front of the site will all read like this.';
    case 'fetch_failed':
      return 'The origin could not be reached at all from here. That is a network or certificate problem, not a wrong token.';
    default:
      return null;
  }
}

export interface OwnershipPanelProps {
  /** The domain the claim is about, for the sentences and the empty state. */
  domain: string;
  claim: OwnershipRecord | null;
  openChallenge: OpenChallengeRecord | null;
  /** The proofs, held in memory since they were issued. The token is in here. */
  challenge: ProofChallenge | null;
  /** The last verify or re-check, printed attempt by attempt. */
  verification: ProofVerification | null;
  issuing: boolean;
  checking: boolean;
  /** A refusal from either call, verbatim. */
  error: string | null;
  /** Which copy button last succeeded, so only that one says so. */
  copied: string | null;
  onCopy(id: string, text: string): void;
  /** POST …/challenge — a fresh token, shown once. */
  onIssue(): void;
  /** POST …/verify or …/recheck; the caller knows which one this state needs. */
  onCheck(): void;
  /** Who is asking and why it matters here. The gate says more than the tab. */
  lede?: VNode | string | null;
  /**
   * Which stylesheet is painting this. Defaults to the dashboard, which has
   * `proof__*` rules; the public console does not and gets the layout inline.
   */
  surface?: 'console' | 'dashboard';
}

export function OwnershipPanel(props: OwnershipPanelProps): VNode {
  const { claim, openChallenge: open, challenge, verification } = props;
  const onConsole = props.surface === 'console';
  const state = claimStateOf(claim, open);
  const domain = claim?.domain || open?.domain || props.domain;
  /**
   * A verified claim gets a re-check and nothing else. Issuing a fresh challenge
   * would invalidate the token behind a claim that is currently working, which
   * is a way to lose a verified domain by pressing the more prominent button.
   */
  const canIssue = state !== 'verified';
  const canCheck = state === 'verified' || state === 'expired' || state === 'failed' || Boolean(open) || Boolean(challenge);

  return html`
    <div class="proof" style=${onConsole ? 'display:grid;gap:14px' : undefined}>
      <p class="proof__state secret__hint">
        <span
          class="chip proof__badge"
          data-tone=${state === 'verified' ? 'ok' : 'warn'}
          >${badge(state)}</span
        >
      </p>
      ${props.lede ? html`<p class="proof__lede ledger2__body">${props.lede}</p>` : null}
      <p class="proof__summary ledger2__body">${summarise(state, domain, claim, open)}</p>

      ${challenge
        ? Proofs({
            challenge,
            copied: props.copied,
            onCopy: props.onCopy,
            onConsole,
          })
        : null}

      <div class="proof__actions gate__actions">
        ${canIssue
          ? html`<button
              type="button"
              class="btn btn--ghost"
              disabled=${props.issuing}
              onClick=${() => props.onIssue()}
            >
              ${props.issuing
                ? 'Issuing…'
                : challenge
                  ? 'Issue a fresh token'
                  : 'Show the three proofs'}
            </button>`
          : null}
        ${canCheck
          ? html`<button
              type="button"
              class="btn btn--ghost"
              disabled=${props.checking}
              onClick=${() => props.onCheck()}
            >
              ${props.checking ? 'Checking…' : 'Check now'}
            </button>`
          : null}
      </div>

      ${open && !challenge
        ? html`<p class="proof__hint secret__hint">
            The token for that challenge was shown once, when it was issued${
              open.tokenHint ? html`; it ends ${SP}<code>${open.tokenHint}</code>` : ''
            }. If the proof is already published, Check now reads it. Asking for the proofs again
            issues a fresh token and retires that one.
          </p>`
        : null}

      ${verification ? Attempts({ verification, onConsole }) : null}
      ${props.error ? html`<p class="proof__err gate__err" role="alert">${props.error}</p>` : null}
    </div>
  `;
}

/**
 * The three proofs, in the API's words.
 *
 * Verbatim because the verifier checks against exactly what the challenge
 * response said, and a console that restated the instructions would give a
 * person two procedures to reconcile at a registrar's control panel. The token
 * is in this response and in no other, so it is copyable from here and lost with
 * the page — which is why an open challenge without it offers Check now instead.
 */
function Proofs(props: {
  challenge: ProofChallenge;
  copied: string | null;
  onCopy(id: string, text: string): void;
  onConsole: boolean;
}): VNode {
  const c = props.challenge;
  const { dns, file, meta } = c.instructions;
  const blockStyle = props.onConsole ? 'margin-bottom:12px' : undefined;
  const codeStyle = props.onConsole
    ? 'margin:10px 0 0;font-family:var(--mono);font-size:12px;white-space:pre-wrap;word-break:break-all'
    : undefined;

  const proof = (id: string, tag: string, howTo: string, value: string, describe: string): VNode => html`
    <div class="proof__block disclaimer" style=${blockStyle}>
      <div class="proof__bar disclaimer__bar">
        <span class="proof__tag disclaimer__tag">${tag}</span>
        ${CopyButton({ id, text: value, copied: props.copied, onCopy: props.onCopy, describe })}
      </div>
      <p class="proof__howto disclaimer__text">${howTo}</p>
      <pre class="proof__code disclaimer__text" style=${codeStyle}>${value}</pre>
    </div>
  `;

  return html`
    <div class="proof__proofs">
      <p class="proof__hint secret__hint">
        Proofs for ${c.domain}. Any ONE of the three is enough. ${c.next.tokenShownOnce}
      </p>
      ${proof(
        'proof-dns',
        `DNS · ${dns.recordType} ${dns.recordName}`,
        dns.howTo,
        dns.value,
        'the DNS record value',
      )}
      ${proof('proof-file', `File · ${file.path}`, file.howTo, file.body, 'the file contents')}
      ${proof('proof-meta', `Meta · ${meta.metaName}`, meta.howTo, meta.tag, 'the meta tag')}
      <p class="proof__hint secret__hint">
        ${c.next.propagation}${SP}${c.next.answerBy}${SP}
        Progress is readable at <code>${c.next.statusUrl}</code>.
        ${c.note ? html`${SP}${c.note}` : null}
      </p>
    </div>
  `;
}

/** Every method the verifier tried, in its own words, and what each miss means. */
function Attempts(props: { verification: ProofVerification; onConsole: boolean }): VNode {
  const v = props.verification;
  // `.ledger2` splits into two columns above 780px and this block has one child,
  // so the console has always pinned it back to one — the attempts would sit in
  // half the width otherwise.
  return html`
    <div
      class="proof__attempts ledger2"
      style=${props.onConsole ? 'margin-top:14px;grid-template-columns:1fr' : undefined}
    >
      <div class="ledger2__col">
        <p class=${v.verified ? 'ledger2__cap ledger2__cap--in' : 'ledger2__cap ledger2__cap--out'}>
          ${v.verified ? `Verified by ${v.method ?? 'a proof'}` : 'Not yet verified'}
        </p>
        <ul class="proof__list">
          ${v.attempts.map((a) => {
            const gloss = a.ok ? null : meaning(a.code);
            return html`
              <li class=${a.ok ? 'proof__row ledger2__row' : 'proof__row ledger2__row ledger2__row--out'}>
                <span class="proof__glyph ledger2__glyph" aria-hidden="true">${a.ok ? '+' : '−'}</span>
                <div>
                  <p class="proof__head ledger2__head">${a.method.toUpperCase()} · ${a.code}</p>
                  <p class="proof__body ledger2__body">${a.detail}</p>
                  ${gloss ? html`<p class="proof__body ledger2__body">${gloss}</p>` : null}
                </div>
              </li>
            `;
          })}
        </ul>
        ${v.verified && v.effect
          ? html`<p class="proof__note ledger2__body">${v.effect.note}</p>`
          : v.nextStep
            ? html`<p class="proof__note ledger2__body">${v.nextStep}</p>`
            : null}
      </div>
    </div>
  `;
}
